import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import EditButton from '../../../ui/EditButton';
import {
  canMooringsMemberRequestWaitlist,
  canvasFromMooringArea,
  deriveMooringPaymentStatus,
  displayNameFromProfile,
  isMooringsAvailabilityVisible,
  isMooringsMemberMapVisible,
  isMooringsMemberVisible,
  isSpaceSuitableForVessel,
  mooringWaitlistSpaceMatches,
  mooringWaitlistTargetLabel,
  summarizeMoorings,
} from '../../utils/moorings';
import '../../../../styles/moorings.css';

function useTeamCollection(teamId, collectionName, enabled) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!enabled || !teamId) {
      setItems([]);
      return undefined;
    }
    return safeOnSnapshot(
      collection(db, 'teams', teamId, collectionName),
      (snapshot) => setItems(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      undefined,
      { label: `moorings-${collectionName}`, teamId }
    );
  }, [collectionName, enabled, teamId]);

  return items;
}

function statusLabel(status) {
  const normalized = String(status || '').replace(/_/g, ' ');
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Unknown';
}

function formatShortDate(value) {
  const date = value?.toDate ? value.toDate() : (value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function createMemberWaitlistDraft() {
  return {
    vesselName: '',
    vesselType: '',
    length: '',
    beam: '',
    draft: '',
    proposedBoatClassId: '',
    memberNote: '',
  };
}

function cleanRequestText(value) {
  return String(value || '').trim();
}

function vesselSnapshotFromRequestDraft(draft = {}) {
  return {
    name: cleanRequestText(draft.vesselName),
    type: cleanRequestText(draft.vesselType),
    typeNormalized: cleanRequestText(draft.vesselType).toLowerCase(),
    length: numberOrNull(draft.length),
    beam: numberOrNull(draft.beam),
    draft: numberOrNull(draft.draft),
  };
}

function missingRestrictionFields(space = {}, vessel = {}) {
  const missing = [];
  if (space.maxLength != null && vessel.length == null) missing.push(`length (max ${space.maxLength}m)`);
  if (space.maxBeam != null && vessel.beam == null) missing.push(`beam (max ${space.maxBeam}m)`);
  if (space.maxDraft != null && vessel.draft == null) missing.push(`draft (max ${space.maxDraft}m)`);
  if (Array.isArray(space.vesselTypes) && space.vesselTypes.length > 0 && !vessel.type) missing.push(`type (${space.vesselTypes.join(', ')})`);
  return missing;
}

export default function MooringsCard({
  teamId,
  userId = '',
  isAdmin = false,
  modules = {},
  memberProfiles = [],
  onManage,
}) {
  const enabled = Boolean(modules?.sailing?.moorings?.enabled);
  const memberVisible = isMooringsMemberVisible(modules);
  const availabilityVisible = isMooringsAvailabilityVisible(modules);
  const memberMapVisible = isMooringsMemberMapVisible(modules);
  const memberWaitlistEnabled = canMooringsMemberRequestWaitlist(modules);
  const paymentSettings = modules?.sailing?.moorings?.payment || {};
  const usesBoatClassPricing = paymentSettings.baselineCalculation === 'class';
  const boatClasses = Array.isArray(paymentSettings.boatClasses)
    ? paymentSettings.boatClasses.filter((boatClass) => String(boatClass?.id || '').trim() && String(boatClass?.name || '').trim())
    : [];
  const shouldLoadAdmin = enabled && isAdmin;
  const [memberView, setMemberView] = useState(null);
  const [publicSummary, setPublicSummary] = useState(null);
  const [publicMap, setPublicMap] = useState(null);
  const [memberWaitlistEntry, setMemberWaitlistEntry] = useState(null);
  const [memberWaitlistPaymentView, setMemberWaitlistPaymentView] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [selectedSpaceId, setSelectedSpaceId] = useState('');
  const [mapZoom, setMapZoom] = useState(0.8);
  const [requestMode, setRequestMode] = useState('');
  const [requestDraft, setRequestDraft] = useState(createMemberWaitlistDraft);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [memberMapLoaded, setMemberMapLoaded] = useState(false);
  const [memberWaitlistLoaded, setMemberWaitlistLoaded] = useState(false);
  const [paymentStatusByAllocationId, setPaymentStatusByAllocationId] = useState({});

  const spaces = useTeamCollection(teamId, 'mooringSpaces', shouldLoadAdmin);
  const allocations = useTeamCollection(teamId, 'mooringAllocations', shouldLoadAdmin);
  const waitlist = useTeamCollection(teamId, 'mooringWaitlist', shouldLoadAdmin);
  const maintenance = useTeamCollection(teamId, 'mooringMaintenance', shouldLoadAdmin);

  const memberById = useMemo(() => new Map(
    (Array.isArray(memberProfiles) ? memberProfiles : []).map((member) => [member.id, member])
  ), [memberProfiles]);

  useEffect(() => {
    if (!enabled || isAdmin || !memberVisible || !teamId || !userId) {
      setMemberView(null);
      return undefined;
    }
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'mooringMemberViews', userId),
      (snapshot) => setMemberView(snapshot.exists() ? (snapshot.data() || {}) : null),
      undefined,
      { label: 'moorings-member-view', teamId }
    );
  }, [enabled, isAdmin, memberVisible, teamId, userId]);

  useEffect(() => {
    if (!enabled || isAdmin || !availabilityVisible || !teamId) {
      setPublicSummary(null);
      return undefined;
    }
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'mooringPublicViews', 'summary'),
      (snapshot) => setPublicSummary(snapshot.exists() ? (snapshot.data() || {}) : null),
      undefined,
      { label: 'moorings-public-summary', teamId }
    );
  }, [availabilityVisible, enabled, isAdmin, teamId]);

  useEffect(() => {
    if (!enabled || isAdmin || !memberMapVisible || !teamId) {
      setPublicMap(null);
      setMemberMapLoaded(false);
      return undefined;
    }
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'mooringPublicViews', 'map'),
      (snapshot) => {
        setPublicMap(snapshot.exists() ? (snapshot.data() || {}) : null);
        setMemberMapLoaded(true);
      },
      () => setMemberMapLoaded(true),
      { label: 'moorings-public-map', teamId }
    );
  }, [enabled, isAdmin, memberMapVisible, teamId]);

  useEffect(() => {
    if (!enabled || isAdmin || !memberVisible || !teamId || !userId) {
      setMemberWaitlistEntry(null);
      setMemberWaitlistLoaded(false);
      return undefined;
    }
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'mooringWaitlist', userId),
      (snapshot) => {
        setMemberWaitlistEntry(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() }) : null);
        setMemberWaitlistLoaded(true);
      },
      () => setMemberWaitlistLoaded(true),
      { label: 'moorings-member-waitlist', teamId }
    );
  }, [enabled, isAdmin, memberVisible, teamId, userId]);

  useEffect(() => {
    const paymentLinkId = memberWaitlistEntry?.paymentLinkId;
    if (!enabled || isAdmin || !memberVisible || !teamId || !userId || !paymentLinkId) {
      setMemberWaitlistPaymentView(null);
      return undefined;
    }
    return safeOnSnapshot(
      doc(db, 'teams', teamId, 'mooringPaymentViews', userId, 'links', paymentLinkId),
      (snapshot) => setMemberWaitlistPaymentView(snapshot.exists() ? (snapshot.data() || {}) : null),
      undefined,
      { label: 'moorings-member-payment', teamId }
    );
  }, [enabled, isAdmin, memberVisible, memberWaitlistEntry?.paymentLinkId, teamId, userId]);

  useEffect(() => {
    if (!shouldLoadAdmin || allocations.length === 0 || !teamId) {
      setPaymentStatusByAllocationId({});
      return undefined;
    }
    let cancelled = false;
    async function loadPaymentStatuses() {
      const entries = await Promise.all(allocations.map(async (allocation) => {
        const boltOnId = allocation.boltOnId || spaces.find((space) => space.id === allocation.spaceId)?.boltOnId || '';
        if (!allocation.memberId || !boltOnId) {
          return [allocation.id, deriveMooringPaymentStatus({ ...allocation, boltOnId })];
        }
        const snap = await getDoc(doc(db, 'teams', teamId, 'members', allocation.memberId, 'boltOns', boltOnId)).catch(() => null);
        return [
          allocation.id,
          deriveMooringPaymentStatus({ ...allocation, boltOnId }, snap?.exists() ? snap.data() : null),
        ];
      }));
      if (!cancelled) setPaymentStatusByAllocationId(Object.fromEntries(entries));
    }
    loadPaymentStatuses();
    return () => {
      cancelled = true;
    };
  }, [allocations, shouldLoadAdmin, spaces, teamId]);

  const summary = useMemo(() => summarizeMoorings({
    spaces,
    allocations,
    waitlist,
    maintenance,
    paymentStatusByAllocationId,
  }), [allocations, maintenance, paymentStatusByAllocationId, spaces, waitlist]);

  const nextAllocation = useMemo(() => {
    if (allocations.length === 0) return null;
    return allocations.find((allocation) => String(allocation.status || 'active') === 'active') || allocations[0];
  }, [allocations]);

  const memberAreas = useMemo(() => (
    Array.isArray(publicMap?.areas) ? publicMap.areas : []
  ), [publicMap]);
  const waitlistStatus = String(memberWaitlistEntry?.status || '').toLowerCase();
  const waitlistPaymentStatus = String(memberWaitlistEntry?.paymentStatus || memberWaitlistPaymentView?.paymentStatus || '').toLowerCase();
  const hasSettledWaitlistPayment = waitlistStatus === 'allocated' || waitlistPaymentStatus === 'paid';
  const settledWaitlistSpaceId = String(memberWaitlistEntry?.allocatedSpaceId || memberWaitlistEntry?.offeredSpaceId || memberWaitlistEntry?.spaceId || '').trim();
  const memberSpaces = useMemo(() => (
    (Array.isArray(publicMap?.spaces) ? publicMap.spaces : []).map((space) => {
      if (hasSettledWaitlistPayment && settledWaitlistSpaceId && space.id === settledWaitlistSpaceId) {
        return { ...space, status: 'occupied' };
      }
      return space;
    })
  ), [hasSettledWaitlistPayment, publicMap, settledWaitlistSpaceId]);
  const activeMemberArea = useMemo(() => (
    memberAreas.find((area) => area.id === selectedAreaId) || memberAreas[0] || null
  ), [memberAreas, selectedAreaId]);
  const activeMemberCanvas = useMemo(() => canvasFromMooringArea(activeMemberArea), [activeMemberArea]);
  const activeMemberSpaces = useMemo(() => (
    memberSpaces.filter((space) => space.areaId === activeMemberArea?.id)
  ), [activeMemberArea, memberSpaces]);
  const selectedMemberSpace = useMemo(() => (
    memberSpaces.find((space) => space.id === selectedSpaceId) || null
  ), [memberSpaces, selectedSpaceId]);
  const requestVesselSnapshot = useMemo(() => vesselSnapshotFromRequestDraft(requestDraft), [requestDraft]);
  const selectedSpaceCompatibility = useMemo(() => {
    if (requestMode !== 'space' || !selectedMemberSpace) return { suitable: true, reasons: [] };
    if (selectedMemberSpace.shape === 'label') return { suitable: false, reasons: ['Choose a mooring space, not a label marker.'] };
    const missing = missingRestrictionFields(selectedMemberSpace, requestVesselSnapshot);
    if (missing.length > 0) {
      return {
        suitable: false,
        reasons: [`Add vessel ${missing.join(', ')} before requesting this space.`],
      };
    }
    return isSpaceSuitableForVessel(selectedMemberSpace, requestVesselSnapshot, { requireVacant: false });
  }, [requestMode, requestVesselSnapshot, selectedMemberSpace]);
  const paidWaitlistAllocation = useMemo(() => {
    if (!hasSettledWaitlistPayment || !memberWaitlistEntry) return null;
    const space = memberSpaces.find((entry) => entry.id === settledWaitlistSpaceId);
    const area = memberAreas.find((entry) => entry.id === space?.areaId);
    return {
      allocationId: memberWaitlistEntry.allocationId || memberWaitlistPaymentView?.allocationId || '',
      status: 'active',
      spaceId: settledWaitlistSpaceId,
      areaId: space?.areaId || '',
      spaceLabel: space?.label || memberWaitlistEntry.offeredSpaceLabel || memberWaitlistEntry.spaceLabel || memberWaitlistPaymentView?.spaceLabel || '',
      areaName: area?.name || memberWaitlistEntry.offeredAreaName || memberWaitlistEntry.areaName || memberWaitlistPaymentView?.areaName || '',
      vesselName: memberWaitlistEntry.vesselName || memberWaitlistEntry.vesselSnapshot?.name || memberWaitlistPaymentView?.vesselName || '',
      vesselType: memberWaitlistEntry.vesselType || memberWaitlistEntry.vesselSnapshot?.type || memberWaitlistPaymentView?.vesselType || '',
      startDate: null,
      endDate: null,
    };
  }, [hasSettledWaitlistPayment, memberAreas, memberSpaces, memberWaitlistEntry, memberWaitlistPaymentView, settledWaitlistSpaceId]);
  const ownAllocations = useMemo(() => {
    const current = Array.isArray(memberView?.allocations) ? memberView.allocations : [];
    if (!paidWaitlistAllocation?.spaceId) return current;
    const hasAllocation = current.some((allocation) => (
      allocation?.allocationId === paidWaitlistAllocation.allocationId
      || allocation?.spaceId === paidWaitlistAllocation.spaceId
    ));
    return hasAllocation ? current : [paidWaitlistAllocation, ...current];
  }, [memberView, paidWaitlistAllocation]);
  const memberMapSummary = useMemo(() => {
    if (memberSpaces.length === 0) return null;
    return memberSpaces.reduce((counts, space) => {
      if (space.shape === 'label') return counts;
      if (space.status === 'occupied') counts.occupied += 1;
      else if (space.status === 'reserved') counts.reserved += 1;
      else if (space.status === 'maintenance_blocked') counts.maintenanceBlocked += 1;
      else counts.vacant += 1;
      return counts;
    }, { vacant: 0, occupied: 0, reserved: 0, maintenanceBlocked: 0, areas: memberAreas.length });
  }, [memberAreas.length, memberSpaces]);
  const ownSpaceKeys = useMemo(() => new Set(ownAllocations.map((allocation) => (
    allocation.spaceId || `${allocation.areaName || ''}::${allocation.spaceLabel || ''}`
  )).filter(Boolean)), [ownAllocations]);
  const hasOpenWaitlistRequest = ['waiting', 'offered'].includes(waitlistStatus) && !hasSettledWaitlistPayment;
  const hasVisibleWaitlistRequest = hasOpenWaitlistRequest || hasSettledWaitlistPayment;
  const hasOfferedWaitlistRequest = waitlistStatus === 'offered' && !hasSettledWaitlistPayment;
  const memberWaitlistTarget = hasVisibleWaitlistRequest ? mooringWaitlistTargetLabel(memberWaitlistEntry) : '';
  const memberRequestedSpace = useMemo(() => {
    if (!hasOpenWaitlistRequest || !memberWaitlistEntry) return null;
    return memberSpaces.find((space) => mooringWaitlistSpaceMatches(
      memberWaitlistEntry,
      space,
      memberAreas.find((area) => area.id === space.areaId)
    )) || null;
  }, [hasOpenWaitlistRequest, memberAreas, memberSpaces, memberWaitlistEntry]);

  useEffect(() => {
    if (!selectedAreaId && memberAreas.length > 0) {
      setSelectedAreaId(memberAreas[0].id);
    }
  }, [memberAreas, selectedAreaId]);

  const resetRequestForm = () => {
    setRequestMode('');
    setRequestDraft(createMemberWaitlistDraft());
    setRequestError('');
  };

  const submitWaitlistRequest = async (event) => {
    event.preventDefault();
    if (!teamId || !userId || !memberWaitlistEnabled) return;
    if (hasOpenWaitlistRequest) {
      setRequestError('You already have an open mooring waitlist request.');
      return;
    }
    const selectedSpace = requestMode === 'space' ? selectedMemberSpace : null;
    if (requestMode === 'space' && !selectedSpace) {
      setRequestError('Choose a space from the map first.');
      return;
    }
    const vesselSnapshot = vesselSnapshotFromRequestDraft(requestDraft);
    const proposedBoatClass = boatClasses.find((boatClass) => boatClass.id === requestDraft.proposedBoatClassId);
    if (usesBoatClassPricing && !proposedBoatClass) {
      setRequestError('Choose a boat class before joining the waitlist.');
      return;
    }
    if (requestMode === 'space') {
      const missing = missingRestrictionFields(selectedSpace, vesselSnapshot);
      const compatibility = selectedSpace.shape === 'label'
        ? { suitable: false, reasons: ['Choose a mooring space, not a label marker.'] }
        : missing.length > 0
          ? { suitable: false, reasons: [`Add vessel ${missing.join(', ')} before requesting this space.`] }
          : isSpaceSuitableForVessel(selectedSpace, vesselSnapshot, { requireVacant: false });
      if (!compatibility.suitable) {
        setRequestError(compatibility.reasons.join(' '));
        return;
      }
    }
    setRequestBusy(true);
    setRequestError('');
    try {
      await setDoc(doc(db, 'teams', teamId, 'mooringWaitlist', userId), {
        memberId: userId,
        status: 'waiting',
        requestType: selectedSpace ? 'space' : 'general',
        spaceId: selectedSpace?.id || '',
        spaceLabel: selectedSpace?.label || '',
        areaName: selectedSpace ? (memberAreas.find((area) => area.id === selectedSpace.areaId)?.name || '') : '',
        vesselName: vesselSnapshot.name,
        vesselType: vesselSnapshot.type,
        vesselTypeNormalized: vesselSnapshot.typeNormalized,
        proposedBoatClassId: proposedBoatClass?.id || '',
        proposedBoatClassNameSnapshot: proposedBoatClass?.name || '',
        length: vesselSnapshot.length,
        beam: vesselSnapshot.beam,
        draft: vesselSnapshot.draft,
        vesselSnapshot,
        memberNote: cleanRequestText(requestDraft.memberNote),
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      resetRequestForm();
    } catch (err) {
      setRequestError(err?.message || 'Failed to join the mooring waitlist.');
    } finally {
      setRequestBusy(false);
    }
  };

  const acceptWaitlistOffer = async () => {
    if (!teamId || !memberWaitlistEntry?.id || acceptBusy) return;
    setAcceptBusy(true);
    setAcceptError('');
    try {
      const acceptOffer = httpsCallable(functions, 'acceptMooringWaitlistOffer');
      const result = await acceptOffer({ teamId, entryId: memberWaitlistEntry.id });
      const url = result.data?.url || (result.data?.linkId ? `/moorings/pay/${result.data.linkId}?teamId=${encodeURIComponent(teamId)}` : '');
      if (!url) throw new Error('No payment link was returned.');
      window.location.href = url;
    } catch (err) {
      setAcceptError(err?.message || 'Could not open mooring payment.');
      setAcceptBusy(false);
    }
  };

  if (!enabled) return null;
  if (!isAdmin && !memberVisible) return null;

  if (!isAdmin) {
    const mine = Array.isArray(memberView?.allocations) ? memberView.allocations : [];
    const canOpenMap = memberMapVisible && memberAreas.length > 0;
    const reserveMapAction = memberMapVisible && (!memberMapLoaded || canOpenMap);
    const reserveWaitlistSlot = memberWaitlistEnabled && (!memberWaitlistLoaded || hasVisibleWaitlistRequest);
    return (
      <>
        <div className="team-card moorings-card">
          <div className="card-header-with-icon">
            <span className="card-title">Moorings</span>
            {reserveMapAction && (
              <button
                type="button"
                className={`btn btn--secondary btn--xs${canOpenMap ? '' : ' moorings-card__reserved-action'}`}
                onClick={() => setMapOpen(true)}
                disabled={!canOpenMap}
                aria-hidden={!canOpenMap}
                tabIndex={canOpenMap ? 0 : -1}
              >
                Open map
              </button>
            )}
          </div>
          <div className="card-body moorings-card__body">
            {mine.length > 0 ? (
              <div className="moorings-card__primary moorings-card__primary--normal">
                <span className="moorings-card__eyebrow">My mooring</span>
                <strong>{mine[0].spaceLabel || mine[0].areaName || 'Allocated space'}</strong>
                <span>{[mine[0].vesselName, mine[0].boatClassName, statusLabel(mine[0].status)].filter(Boolean).join(' • ')}</span>
              </div>
            ) : (
              <div className="moorings-card__primary moorings-card__primary--quiet">
                <span className="moorings-card__eyebrow">Mooring status</span>
                <strong>No allocation recorded</strong>
                <span>Club mooring updates will appear here when available.</span>
              </div>
            )}
            {availabilityVisible && (
              <div className="moorings-card__stats">
                <div className="moorings-card__stat">
                  <span>Vacant</span>
                  <strong>{Number((memberMapSummary || publicSummary)?.vacant || 0)}</strong>
                </div>
                <div className="moorings-card__stat">
                  <span>Reserved</span>
                  <strong>{Number((memberMapSummary || publicSummary)?.reserved || 0)}</strong>
                </div>
                <div className="moorings-card__stat">
                  <span>Areas</span>
                  <strong>{Number((memberMapSummary || publicSummary)?.areas || 0)}</strong>
                </div>
              </div>
            )}
            {reserveWaitlistSlot && (
              <div className={`moorings-card__waitlist${hasVisibleWaitlistRequest ? '' : ' moorings-card__waitlist--reserved'}`} aria-hidden={!hasVisibleWaitlistRequest}>
                <span className="moorings-card__eyebrow">Waitlist request</span>
                <strong>{hasVisibleWaitlistRequest ? memberWaitlistTarget : 'Waiting for request status'}</strong>
                <span>{hasVisibleWaitlistRequest ? [hasSettledWaitlistPayment ? 'Payment received' : statusLabel(memberWaitlistEntry.status), memberWaitlistEntry.vesselName || '', formatShortDate(memberWaitlistEntry.appliedAt) ? `Applied ${formatShortDate(memberWaitlistEntry.appliedAt)}` : ''].filter(Boolean).join(' · ') : 'Loading'}</span>
                {hasOfferedWaitlistRequest && (
                  <button type="button" className="btn btn--primary btn--sm" onClick={acceptWaitlistOffer} disabled={acceptBusy}>
                    {acceptBusy ? 'Opening payment...' : 'Accept and pay'}
                  </button>
                )}
                {acceptError && <span className="moorings-member-request-form__error">{acceptError}</span>}
              </div>
            )}
          </div>
        </div>
        {mapOpen && canOpenMap && (
          <div className="moorings-member-map-modal" role="dialog" aria-modal="true" aria-label="Mooring map">
            <div className="moorings-member-map-modal__content">
              <div className="moorings-member-map-modal__header">
                <div>
                  <h2>Mooring map</h2>
                  <p>Availability and your own allocation are shown for club members.</p>
                </div>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
                  setMapOpen(false);
                  resetRequestForm();
                }}>Close</button>
              </div>
              <div className="moorings-member-map">
                <section className="moorings-member-map__main">
                  <div className="moorings-map-topbar">
                    <div className="moorings-area-tabs" aria-label="Mooring map areas">
                      {memberAreas.map((area) => (
                        <button key={area.id} type="button" className={area.id === activeMemberArea?.id ? 'active' : ''} onClick={() => {
                          setSelectedAreaId(area.id);
                          setSelectedSpaceId('');
                          resetRequestForm();
                        }}>
                          {area.name}
                        </button>
                      ))}
                    </div>
                    <div className="moorings-map-zoom">
                      <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.max(0.5, Number((value - 0.1).toFixed(1))))}>-</button>
                      <span>{Math.round(mapZoom * 100)}%</span>
                      <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.min(1.4, Number((value + 0.1).toFixed(1))))}>+</button>
                    </div>
                  </div>
                  <div className="moorings-member-legend" aria-label="Mooring status legend">
                    <span><i className="moorings-member-legend__swatch moorings-member-legend__swatch--vacant" />Vacant</span>
                    <span><i className="moorings-member-legend__swatch moorings-member-legend__swatch--occupied" />Occupied</span>
                    <span><i className="moorings-member-legend__swatch moorings-member-legend__swatch--reserved" />Reserved</span>
                    <span><i className="moorings-member-legend__swatch moorings-member-legend__swatch--maintenance" />Maintenance</span>
                    <span><i className="moorings-member-legend__swatch moorings-member-legend__swatch--mine" />Mine</span>
                  </div>
                  <div className="moorings-map-scroll moorings-map-scroll--member">
                    <div
                      className="moorings-canvas moorings-canvas--member"
                      style={{
                        width: activeMemberCanvas.width * mapZoom,
                        height: activeMemberCanvas.height * mapZoom,
                        '--moorings-zoom': mapZoom,
                      }}
                    >
                      {activeMemberArea && <div className="moorings-canvas__area-name">{activeMemberArea.name}</div>}
                      {activeMemberSpaces.map((space) => {
                        const selected = selectedSpaceId === space.id;
                        const own = ownSpaceKeys.has(space.id) || ownSpaceKeys.has(`${activeMemberArea?.name || ''}::${space.label || ''}`);
                        const requested = memberRequestedSpace?.id === space.id;
                        const isLabelMarker = space.shape === 'label';
                        return (
                          <button
                            key={space.id}
                            type="button"
                            className={`moorings-canvas-spot moorings-canvas-spot--${space.status || 'vacant'} moorings-canvas-spot--${space.shape || 'rect'}${selected ? ' is-selected' : ''}${own ? ' is-mine' : ''}${requested ? ' is-requested' : ''}`}
                            style={{
                              left: Number(space.x || 0) * mapZoom,
                              top: Number(space.y || 0) * mapZoom,
                              width: Number(space.width || 72) * mapZoom,
                              height: Number(space.height || 48) * mapZoom,
                              transform: `rotate(${Number(space.rotation || 0)}deg)`,
                            }}
                            onClick={() => {
                              setSelectedSpaceId(space.id);
                              setRequestMode('');
                              setRequestError('');
                            }}
                          >
                            <strong>{space.label || 'Space'}</strong>
                            {!isLabelMarker && <span>{own ? 'My mooring' : statusLabel(space.status)}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>
                <aside className="moorings-member-map__side">
                  <h3>{selectedMemberSpace ? selectedMemberSpace.label : 'Select a space'}</h3>
                  {selectedMemberSpace ? (
                    <>
                      <dl className="moorings-member-space-detail">
                        <div><dt>Status</dt><dd>{ownSpaceKeys.has(selectedMemberSpace.id) ? 'My mooring' : statusLabel(selectedMemberSpace.status)}</dd></div>
                        {selectedMemberSpace.maxLength != null && <div><dt>Max length</dt><dd>{selectedMemberSpace.maxLength}m</dd></div>}
                        {selectedMemberSpace.maxBeam != null && <div><dt>Max beam</dt><dd>{selectedMemberSpace.maxBeam}m</dd></div>}
                        {selectedMemberSpace.maxDraft != null && <div><dt>Max draft</dt><dd>{selectedMemberSpace.maxDraft}m</dd></div>}
                        {Array.isArray(selectedMemberSpace.vesselTypes) && selectedMemberSpace.vesselTypes.length > 0 && <div><dt>Types</dt><dd>{selectedMemberSpace.vesselTypes.join(', ')}</dd></div>}
                        {selectedMemberSpace.clubBoat?.label && (
                          <div>
                            <dt>Club boat</dt>
                            <dd>{modules?.sailing?.boats?.showClubBoatNamesOnMooringMap === true ? selectedMemberSpace.clubBoat.label : 'Club boat'}</dd>
                          </div>
                        )}
                      </dl>
                      {memberWaitlistEnabled && !hasVisibleWaitlistRequest && selectedMemberSpace.shape !== 'label' && (
                        <button type="button" className="btn btn--primary btn--sm" onClick={() => setRequestMode('space')}>
                          Request this space
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-muted">Tap a marker to see its safe club-member details.</p>
                  )}
                  {memberWaitlistEnabled && !hasVisibleWaitlistRequest && (
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setRequestMode('general')}>
                      Join general waitlist
                    </button>
                  )}
                  {hasVisibleWaitlistRequest && (
                    <div className="moorings-card__waitlist moorings-card__waitlist--map">
                      <span className="moorings-card__eyebrow">Your waitlist request</span>
                      <strong>{memberWaitlistTarget}</strong>
                      <span>{hasSettledWaitlistPayment ? 'Payment received' : statusLabel(memberWaitlistEntry.status)}{memberWaitlistEntry.vesselName ? ` · ${memberWaitlistEntry.vesselName}` : ''}</span>
                      {hasOfferedWaitlistRequest && (
                        <button type="button" className="btn btn--primary btn--sm" onClick={acceptWaitlistOffer} disabled={acceptBusy}>
                          {acceptBusy ? 'Opening payment...' : 'Accept and pay'}
                        </button>
                      )}
                      {acceptError && <span className="moorings-member-request-form__error">{acceptError}</span>}
                    </div>
                  )}
                  {requestMode && (
                    <form className="moorings-member-request-form" onSubmit={submitWaitlistRequest}>
                      <h4>{requestMode === 'space' ? `Request ${selectedMemberSpace?.label || 'space'}` : 'Join waiting list'}</h4>
                      <label>Vessel<input className="input" value={requestDraft.vesselName} onChange={(event) => setRequestDraft((current) => ({ ...current, vesselName: event.target.value }))} /></label>
                      <label>Type<input className="input" value={requestDraft.vesselType} onChange={(event) => setRequestDraft((current) => ({ ...current, vesselType: event.target.value }))} /></label>
                      {usesBoatClassPricing && boatClasses.length > 0 && (
                        <label>Boat class<select className="input" value={requestDraft.proposedBoatClassId} onChange={(event) => setRequestDraft((current) => ({ ...current, proposedBoatClassId: event.target.value }))} required={usesBoatClassPricing}><option value="">Choose boat class</option>{boatClasses.map((boatClass) => <option key={boatClass.id} value={boatClass.id}>{boatClass.name}</option>)}</select></label>
                      )}
                      <div className="moorings-form-row moorings-form-row--compact">
                        <label>Length<input className="input" type="number" inputMode="decimal" min="0" step="any" value={requestDraft.length} onChange={(event) => setRequestDraft((current) => ({ ...current, length: event.target.value }))} /></label>
                        <label>Beam<input className="input" type="number" inputMode="decimal" min="0" step="any" value={requestDraft.beam} onChange={(event) => setRequestDraft((current) => ({ ...current, beam: event.target.value }))} /></label>
                        <label>Draft<input className="input" type="number" inputMode="decimal" min="0" step="any" value={requestDraft.draft} onChange={(event) => setRequestDraft((current) => ({ ...current, draft: event.target.value }))} /></label>
                      </div>
                      {requestMode === 'space' && selectedSpaceCompatibility.reasons.length > 0 && (
                        <p className={selectedSpaceCompatibility.suitable ? 'text-muted' : 'moorings-member-request-form__error'}>
                          {selectedSpaceCompatibility.reasons.join(' ')}
                        </p>
                      )}
                      <label>Note<textarea className="input" value={requestDraft.memberNote} onChange={(event) => setRequestDraft((current) => ({ ...current, memberNote: event.target.value }))} /></label>
                      {requestError && <p className="moorings-member-request-form__error">{requestError}</p>}
                      <div className="form-actions">
                        <button type="submit" className="btn btn--primary btn--sm" disabled={requestBusy || (requestMode === 'space' && !selectedSpaceCompatibility.suitable)}>Submit request</button>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={resetRequestForm} disabled={requestBusy}>Cancel</button>
                      </div>
                    </form>
                  )}
                </aside>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const nextMemberName = nextAllocation
    ? displayNameFromProfile(memberById.get(nextAllocation.memberId), nextAllocation.memberNameSnapshot || 'Member')
    : '';

  return (
    <div className="team-card moorings-card">
      <div className="card-header-with-icon">
        <span className="card-title">Moorings</span>
        <div className="moorings-card__header-actions">
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => onManage?.({ initialTab: 'map' })}>
            Manage
          </button>
          <EditButton section="moorings" onEdit={() => onManage?.({ initialTab: 'map' })} />
        </div>
      </div>
      <div className="card-body moorings-card__body">
        <button type="button" className="moorings-card__primary moorings-card__primary--normal" onClick={() => onManage?.({ initialTab: 'allocations' })}>
          <span className="moorings-card__eyebrow">Allocation overview</span>
          <strong>{summary.occupied} occupied · {summary.vacant} vacant</strong>
          <span>{nextAllocation ? `${nextMemberName} • ${nextAllocation.vesselName || 'Vessel not named'}` : 'No allocations recorded yet.'}</span>
        </button>
        <div className="moorings-card__stats">
          <button type="button" className="moorings-card__stat" onClick={() => onManage?.({ initialTab: 'allocations' })}>
            <span>Unpaid</span>
            <strong>{summary.unpaidAllocations}</strong>
          </button>
          <button type="button" className="moorings-card__stat" onClick={() => onManage?.({ initialTab: 'waitlist' })}>
            <span>Waitlist</span>
            <strong>{summary.waitlistOpen}</strong>
          </button>
          <button type="button" className="moorings-card__stat" onClick={() => onManage?.({ initialTab: 'maintenance' })}>
            <span>Maintenance</span>
            <strong>{summary.maintenanceOpen + summary.maintenanceBlocked}</strong>
          </button>
        </div>
      </div>
    </div>
  );
}
